import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';
import { useDidMount } from 'rooks';
import { WrapperWithLink } from '@/core/WrapperWithLink.atom';
import { sqWorkbookStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { isSystemTest } from '@/core/utilities';
import { APPSERVER_API_PREFIX } from '@/main/app.constants';
import { getPresentWorksheetLink, getShortViewWorksheetLink, getWorksheetLink } from '@/main/routing.utilities';
import { base64guid } from '@/utilities/guid.utilities';
import { isMobileDevice } from '@/utilities/utilities';
import { isEditWorkbookMode, isViewOnlyWorkbookMode } from '@/utilities/workbookMode.utilities';
import { sqWorkbooksApi } from '@seeqdev/api/endpoint/WorkbooksApi';
import { ButtonWithPopover } from '@seeqdev/qomponents';
import type { WORKBOOK_DISPLAY } from '@/workbook/workbook.constants';

interface WorksheetThumbnailProps {
  worksheetId: string;
  workbookId: string;
  isActive: boolean;
  onClick?: () => void;
  tooltipPlacement?: 'right' | 'left';
  popoverHoverDelay?: number;
}

const getThumbnailLink = (workbookId: string, worksheetId: string, displayMode?: WORKBOOK_DISPLAY) => {
  if (isEditWorkbookMode(displayMode)) {
    return getWorksheetLink(workbookId, worksheetId, undefined, undefined, true);
  } else if (isViewOnlyWorkbookMode(displayMode)) {
    return getShortViewWorksheetLink(worksheetId, true);
  } else {
    return getPresentWorksheetLink(workbookId, worksheetId, true);
  }
};

export const EMPTY_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
export const WorksheetThumbnail = ({
  worksheetId,
  workbookId,
  isActive,
  onClick,
  tooltipPlacement = 'right',
  popoverHoverDelay = 1300,
}: WorksheetThumbnailProps) => {
  const displayMode = useFluxPath(sqWorkbookStore, (store) => store.workbookDisplay);
  const thumbnailLink = getThumbnailLink(workbookId, worksheetId, displayMode);
  const thumbnailRef = useRef<HTMLDivElement>(null);
  const usingLatestThumbnail = useFluxPath(
    sqWorkbookStore,
    (store) => store.getWorksheet(worksheetId)?.usingLatestThumbnail,
  );

  const [src, setSrc] = React.useState<string>(
    `${APPSERVER_API_PREFIX}/workbooks/${workbookId}/worksheets/${worksheetId}/thumbnail`,
  );

  useEffect(() => {
    if (usingLatestThumbnail) {
      setSrc(
        `${APPSERVER_API_PREFIX}/workbooks/${workbookId}/worksheets/${worksheetId}/thumbnail?hash=${base64guid()}`,
      );
    }
  }, [workbookId, worksheetId, usingLatestThumbnail]);

  useDidMount(() => {
    if (isActive && thumbnailRef.current) {
      const container = document.getElementById('worksheetScrollArea');

      if (container && thumbnailRef.current) {
        const containerRect = container.getBoundingClientRect();
        const thumbnailRect = thumbnailRef.current.getBoundingClientRect();
        const offset = thumbnailRect.top - containerRect.top;
        container.scrollTo({ top: container.scrollTop + offset });
      }
    }
  });

  return (
    <div
      ref={thumbnailRef}
      onClick={onClick}
      data-testid={`${worksheetId}_thumbnailContainer`}
      className="cursorPointer flexRowContainer flexAlignCenter">
      <WrapperWithLink link={thumbnailLink}>
        <ButtonWithPopover
          placement={tooltipPlacement}
          hasArrow={true}
          isHoverEnabled={src !== EMPTY_IMAGE && !isSystemTest() && !isMobileDevice()}
          isOpen={false}
          isPortal={true}
          hoverOpenDelay={popoverHoverDelay}
          align="center"
          containerTestId={`${worksheetId}_thumbnailPopover`}
          trigger={
            <img
              data-testid={`${worksheetId}_thumbnail`}
              className={clsx(
                'p3 screenshotBackground height-80 width-100 roundedBorder',
                isActive ? 'activeWorksheetBorder' : 'darkGreyBorder',
              )}
              src={src}
              loading="lazy"
              decoding="async"
              onError={() => {
                setSrc(EMPTY_IMAGE);
                sqWorkbooksApi.createWorksheetThumbnail({ workbookId, worksheetId });
              }}
            />
          }>
          <div className="p2">
            <img className="backgroundColorWhite" src={src} width="300" height="240" loading="lazy" decoding="async" />
          </div>
        </ButtonWithPopover>
      </WrapperWithLink>
    </div>
  );
};
